import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Row, Col, Table, Alert, message, Form, DatePicker, Button } from "antd";
import { apis, AUTH_HEADER_NAME } from "common/apis";
import { IPurchaseIndentRow, IPurchaseIndentUpload } from "common/interface";
import { IAppError } from "common/interface/IApiError";
import { routes } from "common/routes";
import { AppPageHeader } from "components/AppPageHeader";
import { AppUpload } from "components/AppUpload";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import {
  removeProcessingPurchaseIndent,
  setProcessingPurchaseIndent,
} from "store/purchase-indent/purchaseIndentSlice";
import { generateHeaders } from "common/utils";
import { ColumnsType } from "antd/lib/table";
import { useDocumentTitle } from "hooks/usePageTitle";

const PurchaseIndentUploadPage = () => {
  useDocumentTitle("Upload Purchase Indent");
  const { authToken } = useAppSelector((state) => state.app.user);
  const { processingPurchaseIndent } = useAppSelector(
    (state) => state.purchaseIndent
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasErrorRows, setHasErrorRows] = useState(false);
  const [deliveryDate, setDate] = useState<string>();

  useEffect(() => {
    const { timeUploaded } = processingPurchaseIndent;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingPurchaseIndent());
      }
    }

    if (processingPurchaseIndent && processingPurchaseIndent.key) {
      navigate(`/${routes.PURCHASE_INDENT.SAVE}`, {});
    }

    return () => {
      message.destroy();
    };
  }, [processingPurchaseIndent, navigate, dispatch]);

  useEffect(() => {
    const failed = processingPurchaseIndent?.failedRows?.length;
    if (failed) {
      setHasErrorRows(true);
    }
  }, [processingPurchaseIndent?.failedRows]);

  const headers: { [name: string]: string } = {
    ...(authToken && { [AUTH_HEADER_NAME]: authToken }),
  };

  const onUploaded = (data: IPurchaseIndentUpload) => {
    dispatch(setProcessingPurchaseIndent(data));
  };

  const onUploading = () => {
    dispatch(removeProcessingPurchaseIndent());
  };

  const { headerMapping } = processingPurchaseIndent;
  const headerMappingWithError = headerMapping + ",errors:Errors";

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMappingWithError),
    [headerMappingWithError]
  );

  const tableHeaders = generateMemoizedHeaders || {};
  const successRows: IPurchaseIndentRow[] = processingPurchaseIndent?.successRows || [];
  const failedRows: IPurchaseIndentRow[] = processingPurchaseIndent?.failedRows || [];
  const successData = successRows.map((row: IPurchaseIndentRow, i: number) => ({
    ...row,
    key: `data-success-${i}`,
  }));
  const failedData = failedRows.map((row: IPurchaseIndentRow, i: number) => ({
    ...row,
    key: `data-failed-${i}`,
  }));
  const data = [...failedData, ...successData];

  const columns: ColumnsType<IPurchaseIndentRow & { key: string }> = [
    ...Object.keys(tableHeaders).map((key) => ({
      key,
      title: tableHeaders[key],
      dataIndex: key,
      render: (text: string, record: IPurchaseIndentRow & { key: string }) => {
        if (key === "errors") {
          const errorMessages = (record.errors as IAppError[])
            ?.map((error: IAppError) => error.message)
            .join(", ");
          return <>{errorMessages}</>;
        }
        return <>{text}</>;
      },
    })),
  ];

  const addBorderToErrorRows = (
    record: IPurchaseIndentRow & { key: string }
  ) => {
    if (record.errors && record.errors.length) {
      return "error-row";
    }
    return "";
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Upload Purchase Indent" />
        </Col>
      </Row>
      <Row>
        <Form style={{ width: "80%" }}>
          <Form.Item label="Select Delivery Date" name="orderDate" required>
            <DatePicker
              onChange={(val) => setDate(val?.format("yyyy-MM-DD"))}
            />
          </Form.Item>
        </Form>
        <span className="mr-4"></span>
        <div>
          <Button
            type="primary"
            onClick={() => {
              window.location.href =
                "https://d69ugcdrlg41w.cloudfront.net/upload_samples/warehouse/purchase_indent_sample.csv";
            }}
          >
            Download Sample File
          </Button>
        </div>
      </Row>

      {deliveryDate && (
        <Row>
          <Col lg={16} xs={24}>
            <AppUpload
              uploadUrl={apis.uploadPurchaseIndent}
              headers={headers}
              onUploaded={onUploaded}
              onUploading={onUploading}
              hasErrorRows={hasErrorRows}
              data={{ orderDate: deliveryDate }}
            />
          </Col>
        </Row>
      )}

      {!!Object.keys(processingPurchaseIndent).length &&
        !processingPurchaseIndent.key && (
          <>
            <Row>
              <Col lg={16} xs={24}>
                <Alert
                  type="error"
                  message="Errors in sheet data."
                  description="Please correct errors and upload again."
                  showIcon
                  banner
                />
              </Col>
            </Row>

            <Row>
              <Table
                size="small"
                columns={columns}
                dataSource={data}
                rowClassName={(record) => addBorderToErrorRows(record)}
                scroll={{ y: 500, x: "100vh" }}
                className="table-with-errors"
              />
            </Row>
          </>
        )}
    </>
  );
};

export default PurchaseIndentUploadPage;
